import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { MaxsoldAuctionDetailDto } from '@jordan/types';
import { Alert, Badge, Breadcrumb, Button, DataTable, PageHeader, Spinner } from '@jordan/ui';
import { AppChrome } from '../../components/AppChrome';
import { ApiError, apiGet } from '../../lib/api';
import { maxsoldSidebar } from './maxsoldSidebar';
import { formatClose } from './maxsoldSearchUtils';
import { useWatchlist } from './watchlist';
import { ListingGrid, listingColumns, type ListingView } from './MaxsoldListing';

export function MaxsoldAuctionPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { auctionId } = useParams<{ auctionId: string }>();

  const [data, setData] = useState<MaxsoldAuctionDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [keyword, setKeyword] = useState('');
  const wl = useWatchlist();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!auctionId) return;
    setLoading(true);
    setErr(null);
    apiGet<MaxsoldAuctionDetailDto>(`/api/maxsold/auction/${auctionId}`)
      .then(setData)
      .catch((e: unknown) => {
        const body = e instanceof ApiError ? (e.body as { message?: string } | null) : null;
        setErr(body?.message ?? 'Could not load that auction.');
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [auctionId]);

  const openLot = useCallback((id: number) => navigate(`/maxsold/lot/${id}`), [navigate]);

  const kw = keyword.trim().toLowerCase();
  const views = useMemo<ListingView[]>(
    () =>
      (data?.lots ?? [])
        .filter((l) => kw === '' || l.title.toLowerCase().includes(kw))
        .map((l) => ({
          id: l.id,
          imageUrl: l.imageUrl,
          title: l.title,
          subtitle: l.lotNumber != null ? `Lot ${l.lotNumber}` : null,
          price: l.currentBid,
          bidCount: l.bidCount,
          closeTime: l.closeTime,
          favourite: wl.configured
            ? { active: wl.isFavourite(l.id), pending: wl.isPending(l.id), onToggle: () => wl.toggle(l.id) }
            : null,
        })),
    [data, kw, wl],
  );

  const columns = useMemo(
    () => listingColumns({ now, titleHeader: 'Lot', price: true, bids: true, favourite: wl.configured }),
    [now, wl.configured],
  );

  const a = data?.auction;
  const placeLine = a ? [a.address, a.city, a.province].filter(Boolean).join(', ') : '';

  return (
    <AppChrome secondarySidebar={maxsoldSidebar(navigate, pathname)}>
      <PageHeader
        title={
          <>
            Maxsold - <span className="fw-normal">Auction</span>
          </>
        }
        breadcrumb={
          <Breadcrumb
            items={[
              { icon: 'ph-house', href: '/' },
              { label: 'Maxsold' },
              { label: 'Search', href: '/maxsold/search' },
              { label: 'Auction' },
            ]}
          />
        }
      />

      <div className="content">
        <div className="mb-3">
          <Button variant="light" icon="ph-arrow-left" onClick={() => navigate('/maxsold/search')}>
            Back to search
          </Button>
        </div>

        {err && (
          <Alert variant="danger" icon="ph-warning">
            {err}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner />
          </div>
        ) : (
          a && (
            <>
              {/* Auction header */}
              <div className="card mb-3">
                <div className="card-body">
                  <div className="d-flex flex-column flex-md-row gap-3">
                    {a.imageUrls[0] && (
                      <img
                        src={a.imageUrls[0]}
                        alt=""
                        style={{ width: 160, height: 120, objectFit: 'cover', borderRadius: 6 }}
                        className="flex-shrink-0"
                      />
                    )}
                    <div className="flex-grow-1">
                      <h5 className="mb-1">{a.title}</h5>
                      <div className="mb-2 d-flex flex-wrap gap-2">
                        {a.category && <Badge variant="primary" soft>{a.category}</Badge>}
                        {a.region && <Badge variant="secondary" soft>{a.region}</Badge>}
                        <Badge variant="light">{a.lotCount} lots</Badge>
                      </div>
                      {placeLine && (
                        <div className="text-muted fs-sm mb-1">
                          <i className="ph-map-pin me-1" />
                          {placeLine}
                        </div>
                      )}
                      <div className="text-muted fs-sm mb-3">
                        <i className="ph-clock me-1" />
                        Closes {formatClose(a.closeTime)}
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {a.url && (
                          <a href={a.url} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm">
                            <i className="ph-arrow-square-out me-1" />
                            View on MaxSold
                          </a>
                        )}
                        {a.mapUrl && (
                          <a href={a.mapUrl} target="_blank" rel="noopener noreferrer" className="btn btn-light btn-sm">
                            <i className="ph-map-pin me-1" />
                            Directions
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Lots */}
              <div className="card">
                <div className="card-header d-flex flex-wrap align-items-center gap-2">
                  <h5 className="mb-0 me-auto">
                    Lots<span className="text-muted fw-normal ms-2">{views.length} shown</span>
                  </h5>
                  <div className="form-control-feedback form-control-feedback-start" style={{ maxWidth: 220 }}>
                    <input
                      type="search"
                      className="form-control"
                      placeholder="Filter lots…"
                      value={keyword}
                      onChange={(e) => setKeyword(e.target.value)}
                    />
                    <div className="form-control-feedback-icon">
                      <i className="ph-funnel opacity-50" />
                    </div>
                  </div>
                  <div className="btn-group" role="group" aria-label="View">
                    {(['grid', 'list'] as const).map((v) => (
                      <button
                        key={v}
                        type="button"
                        className={`btn btn-sm ${view === v ? 'btn-primary' : 'btn-light'}`}
                        onClick={() => setView(v)}
                        title={v}
                      >
                        <i className={v === 'grid' ? 'ph-squares-four' : 'ph-list'} />
                      </button>
                    ))}
                  </div>
                </div>
                <div className="card-body">
                  {view === 'grid' ? (
                    <ListingGrid
                      views={views}
                      now={now}
                      onOpen={openLot}
                      emptyMessage="No lots match."
                      surchargePercent={data?.surchargePercent ?? 0}
                    />
                  ) : (
                    <DataTable
                      columns={columns}
                      rows={views}
                      getRowKey={(v) => String(v.id)}
                      onRowClick={(v) => openLot(v.id)}
                      initialSort={{ key: 'price', dir: 'desc' }}
                      emptyMessage="No lots match."
                    />
                  )}
                </div>
              </div>
            </>
          )
        )}
      </div>
    </AppChrome>
  );
}
